import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import {
    transferAttendedCall,
    transferBlindCall,
} from 'flow/actions/phone-actions';
import CallTransferDialog from 'flow/components/call-transfer-dialog/CallTransferDialog';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { Contact } from 'flow/types/Contact';
import { ContactKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';
import { isExtensionStatusInCall } from 'flow/utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    extensionKey: string;
    number: string;
    transferContact: TypedMap<Contact>;
    size: 'small' | 'medium' | 'large';
    inactiveBgColor?: string;
    className?: string;
    id?: string;
}

const CallTransferButton = ({
    extensionKey,
    number,
    transferContact,
    size,
    inactiveBgColor = 'bg-gray-300',
    className,
    id,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    let iconSize = 'size-8'; // Default size

    if (size === 'small') {
        iconSize = 'size-8';
    } else if (size === 'medium') {
        iconSize = 'size-10';
    } else if (size === 'large') {
        iconSize = 'size-12';
    }

    const [transferSelectionDialogOpen, setTransferSelectionDialogOpen] =
        React.useState(false);

    const thisExtensionIsUnavailable = useAppSelector((state) =>
        isExtensionStatusInCall(
            state.extensions.getIn(['statuses', extensionKey]),
        ),
    );
    const extensions = useAppSelector((state) => state.extensions).get(
        'extensions',
    );

    const getExtensionFromContactKeyTW = (contactKey: ContactKey) => {
        return extensions.find(
            (extension) => extension.getIn(['contact', 'key']) === contactKey,
        );
    };

    const getExtensionFromContact = (contact: TypedMap<Contact>) => {
        return contact
            ? getExtensionFromContactKeyTW(contact.get('key'))
            : null;
    };

    const openTransferSelectionDialog = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        setTransferSelectionDialogOpen(true);
    };
    const closeTransferSelectionDialog = () => {
        setTransferSelectionDialogOpen(false);
    };

    const handleTransferBlind = (event: MouseEvent) => {
        event.stopPropagation();
        dispatch(transferBlindCall(number, 'call transfer button'));
        closeTransferSelectionDialog();
    };

    const handleTransferAttended = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        // @ts-expect-error
        dispatch(transferAttendedCall(number, transferContact));
        closeTransferSelectionDialog();
    };

    const handleNewCall = () => {
        const transferExtension = getExtensionFromContact(transferContact);
        dispatch(showCallDialog(number, transferExtension));
        closeTransferSelectionDialog();
    };

    return (
        <>
            <IconButton
                id={id}
                aria-label={i18next.t('Call.Transfer')}
                className={twMerge(
                    iconSize,
                    inactiveBgColor,
                    thisExtensionIsUnavailable ? 'text-red' : 'text-gray-600',
                    className,
                )}
                onClick={openTransferSelectionDialog}
                dataTestId="call-transfer-button"
            >
                <Icon iconKey="transfer_call" />
            </IconButton>
            {transferSelectionDialogOpen ? (
                <CallTransferDialog
                    transferNumber={number}
                    transferContact={transferContact}
                    handleTransferBlind={handleTransferBlind}
                    handleTransferAttended={handleTransferAttended}
                    handleNewCall={handleNewCall}
                    handleClose={closeTransferSelectionDialog}
                    thisExtensionIsUnavailable={thisExtensionIsUnavailable}
                />
            ) : null}
        </>
    );
};

export default CallTransferButton;
